/**
 * Common formatting utilities used across the application
 */

/**
 * Format a number as Indian currency
 * @param {number} amount - The amount to format
 * @param {boolean} includeSymbol - Whether to include ₹ symbol
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, includeSymbol = true) => {
  if (!amount && amount !== 0) return includeSymbol ? '₹0' : '0';
  
  const formatted = new Intl.NumberFormat('en-IN', {
    style: includeSymbol ? 'currency' : 'decimal',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
  
  return formatted;
};

/**
 * Format a number as Indian currency without decimals
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrencyRounded = (amount) => {
  if (!amount && amount !== 0) return '₹0';
  return Math.round(amount).toLocaleString('en-IN');
};

/**
 * Format a date string to DD/MM/YYYY
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Format a date string to DD-MM-YYYY
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date string
 */
export const formatDateDash = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Format a date string to DD MMM YYYY (e.g., 15 Jan 2024)
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date string
 */
export const formatDateShort = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Format a date string to DD MMM YY (e.g., 15 Jan 24)
 * @param {string|Date} dateString - The date to format
 * @returns {string} Formatted date string
 */
export const formatDateShortYear = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit'
  });
};

/**
 * Format size code with milliliters
 * @param {string} sizeCode - The size code
 * @param {number} size - The size in ml
 * @returns {string} Formatted size string
 */
export const formatSize = (sizeCode, size) => {
  return `${sizeCode}(${size}ml)`;
};

/**
 * Format a number with Indian locale (commas)
 * @param {number} num - The number to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  return num.toLocaleString('en-IN');
};

/**
 * Calculate cases and loose bottles from total bottles
 * @param {number} totalBottles - Total number of bottles
 * @param {number} packQuantity - Number of bottles per case
 * @returns {Object} Object with cases and loose bottles
 */
export const calculateCasesAndLoose = (totalBottles, packQuantity) => {
  const pq = parseInt(packQuantity, 10) || 0;
  
  if (pq <= 0) {
    return { cases: null, loose: totalBottles || 0 };
  }
  
  const cases = Math.floor((totalBottles || 0) / pq);
  const loose = (totalBottles || 0) % pq;
  
  return { cases, loose };
};

